import { UniqueIdentifier } from "@dnd-kit/core";
import { ImsPermissions } from "@redotech/account-common/permissions";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { PurchaseOrdersSystemView } from "@redotech/redo-fulfillment-common/views/default-purchase-order-views";
import { useTranslation } from "@redotech/redo-i18n";
import PackageCheckIcon from "@redotech/redo-icons/package-check.svg";
import PackageCheckFilledIcon from "@redotech/redo-icons/package-check_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PurchaseOrderViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { CREATE_VIEW_NAME } from "@redotech/redo-views-common/views";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";
import { useDefaultViewName } from "./get-default-view-name";

export const PurchaseOrdersItem = memo(function PurchaseOrdersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { views, folders, openEditFolderModal, updateOrdering } =
    useRequiredContext(PurchaseOrderViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/purchase-orders`;

  const hasPermissionToViewItem = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );

  const defaultViewName = useDefaultViewName(
    views,
    PurchaseOrdersSystemView.ALL,
  );

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"purchase-orders">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"purchase-orders">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${encodeURIComponent(defaultViewName)}`,
      }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={PackageCheckFilledIcon}
      friendlyName={t("Purchase orders", {
        context: "inventory and purchasing navigation label",
      })}
      id="purchase-orders"
      UnfilledIcon={PackageCheckIcon}
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <PurchaseOrdersAddViewItem href={`${urlPrefix}/${CREATE_VIEW_NAME}`} />
      <PurchaseOrdersCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const PurchaseOrdersAddViewItem = memo(function PurchaseOrdersAddViewItem({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={t("+ Add new view", {
        context: "inventory and purchasing navigation label",
      })}
      id="purchase-orders-create-view"
    />
  );
});

const PurchaseOrdersCreateFolderItem = memo(
  function PurchaseOrdersCreateFolderItem() {
    const { t } = useTranslation();
    const { openCreateFolderModal } = useRequiredContext(
      PurchaseOrderViewsContext,
    );

    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName={t("+ Add new folder", {
          context: "inventory and purchasing navigation label",
        })}
        id="purchase-orders-create-folder"
      />
    );
  },
);
